import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from 'src/@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'src/@/components/ui/select';

import v1_actionMessengerDestinatarios from 'src/actions/v1/meta/messenger/v1_actionMessengerDestinatarios';
import v1_actionMessengerEnviarTeste from 'src/actions/v1/meta/messenger/v1_actionMessengerEnviarTeste';

import { TEMPLATES_MESSENGER_DESIGN as D } from './TemplatesMessenger.design';
import MessengerPreview from './MessengerPreview';
import {
    variaveisDoTemplate, textoDoBody, botoesDoTemplate, componentePorTipo
} from './templatesMessenger.utils';

/**
 * Envio de teste de uma utility message.
 * A Meta exige um PSID (id do usuário escopado à Página), que só existe depois de o
 * usuário ter conversado com a Página — daí a lista de conversas recentes ao lado do
 * campo manual.
 */
export default function EnviarTesteDialog({ aberto, template, canal, pagina, clienteId, onFechar, onEnviado }) {
    const [psid, setPsid] = useState('');
    const [valores, setValores] = useState({});
    const [valoresBotoes, setValoresBotoes] = useState({});
    const [destinatarios, setDestinatarios] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState(null);

    const vars = useMemo(() => (template ? variaveisDoTemplate(template) : null), [template]);
    const header = componentePorTipo(template?.components, 'HEADER');
    const headerFormato = String(header?.format || '').toUpperCase();

    useEffect(() => {
        if (!aberto) return;

        setPsid('');
        setValores({});
        setValoresBotoes({});
        setErro(null);

        v1_actionMessengerDestinatarios(clienteId, { canalId: canal?._id })
            .then((res) => setDestinatarios(res.data?.data?.destinatarios || []))
            .catch(() => setDestinatarios([]));
    }, [aberto, clienteId, canal?._id]);

    const handleEnviar = async () => {
        setErro(null);

        if (!psid.trim()) {
            setErro('Informe o PSID do destinatário');
            return;
        }

        const faltando = [...(vars?.header || []), ...(vars?.body || [])]
            .filter((ph) => !String(valores[ph] || '').trim());
        if (faltando.length) {
            setErro(`Preencha o valor de {{${faltando[0]}}}`);
            return;
        }

        setEnviando(true);
        try {
            await v1_actionMessengerEnviarTeste(clienteId, {
                canalId: canal._id,
                templateId: template._id,
                psid: psid.trim(),
                parametros: {
                    header: (vars?.header || []).map((ph) => ({
                        ...(vars.isNamed ? { param_name: ph } : {}),
                        text: valores[ph]
                    })),
                    body: (vars?.body || []).map((ph) => ({
                        ...(vars.isNamed ? { param_name: ph } : {}),
                        text: valores[ph]
                    })),
                    buttons: (vars?.buttons || []).map((btn) => ({
                        type: btn.type,
                        value: valoresBotoes[btn.index] || ''
                    }))
                }
            });

            onEnviado();
        } catch (e) {
            setErro(e.response?.data?.message || 'Não foi possível enviar a mensagem');
        } finally {
            setEnviando(false);
        }
    };

    if (!template) return null;

    const semVariaveis = !(vars?.header?.length || vars?.body?.length || vars?.buttons?.length);

    return (
        <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
            <DialogContent className="max-w-3xl z-[10000]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Iconify icon="eva:paper-plane-outline" width={16} className="text-blue-600" />
                        </div>
                        Enviar teste
                    </DialogTitle>
                    <DialogDescription>
                        {template.templateName || template.name} · {pagina?.nome || canal?.nome}
                    </DialogDescription>
                </DialogHeader>

                {erro && (
                    <div className="flex items-start gap-2.5 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5">
                        <Iconify icon="eva:alert-circle-outline" width={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-800">{erro}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Formulário */}
                    <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
                        <div>
                            <Label className={D.drawer.content.section.label}>Destinatário (PSID) *</Label>

                            {destinatarios.length > 0 && (
                                <Select value="" onValueChange={setPsid}>
                                    <SelectTrigger className={`${D.drawer.content.section.input.base} mb-2`}>
                                        <SelectValue placeholder="Escolher de conversas recentes" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[10001]" position="popper" sideOffset={4}>
                                        {destinatarios.map((d) => (
                                            <SelectItem key={d.psid} value={d.psid}>
                                                {d.nome ? `${d.nome} — ${d.psid}` : d.psid}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}

                            <Input
                                value={psid}
                                onChange={(e) => setPsid(e.target.value)}
                                placeholder="Ou cole o PSID manualmente"
                                className={`${D.drawer.content.section.input.base} font-mono text-xs`}
                            />
                            {destinatarios.length === 0 && (
                                <p className="text-[11px] text-gray-400 mt-1.5">
                                    Nenhuma conversa nos últimos 30 dias — o PSID só existe depois de a pessoa falar com a Página.
                                </p>
                            )}
                        </div>

                        {semVariaveis && (
                            <p className="text-xs text-gray-500">Este template não tem variáveis para preencher.</p>
                        )}

                        {(vars?.header?.length > 0) && (
                            <div>
                                <Label className={D.drawer.content.section.label}>Título</Label>
                                <div className="space-y-2">
                                    {vars.header.map((ph) => (
                                        <div key={`h-${ph}`} className="flex items-center gap-2.5">
                                            <span className={`${D.drawer.variaveis.chip} flex-shrink-0`}>{`{{${ph}}}`}</span>
                                            <Input
                                                value={valores[ph] || ''}
                                                onChange={(e) => setValores((v) => ({ ...v, [ph]: e.target.value }))}
                                                className={`${D.drawer.content.section.input.base} h-9`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(vars?.body?.length > 0) && (
                            <div>
                                <Label className={D.drawer.content.section.label}>Mensagem</Label>
                                <div className="space-y-2">
                                    {vars.body.map((ph) => (
                                        <div key={`b-${ph}`} className="flex items-center gap-2.5">
                                            <span className={`${D.drawer.variaveis.chip} flex-shrink-0`}>{`{{${ph}}}`}</span>
                                            <Input
                                                value={valores[ph] || ''}
                                                onChange={(e) => setValores((v) => ({ ...v, [ph]: e.target.value }))}
                                                className={`${D.drawer.content.section.input.base} h-9`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(vars?.buttons?.length > 0) && (
                            <div>
                                <Label className={D.drawer.content.section.label}>Botões</Label>
                                <div className="space-y-2">
                                    {vars.buttons.map((btn) => (
                                        <div key={`bt-${btn.index}`}>
                                            <p className="text-[11px] text-gray-500 mb-1">
                                                {btn.text} — {btn.type === 'URL' ? 'sufixo da URL' : 'payload'}
                                            </p>
                                            <Input
                                                value={valoresBotoes[btn.index] || ''}
                                                onChange={(e) => setValoresBotoes((v) => ({ ...v, [btn.index]: e.target.value }))}
                                                placeholder={btn.exemplo || (btn.type === 'URL' ? '1234' : 'valor')}
                                                className={`${D.drawer.content.section.input.base} h-9`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Preview do resultado final */}
                    <div className="bg-gradient-to-b from-slate-50 to-slate-100/50 rounded-xl p-4">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Como vai chegar</p>
                        <MessengerPreview
                            pageName={pagina?.nome || canal?.nome}
                            pageProfile={pagina?.profile}
                            headerType={header ? (headerFormato === 'IMAGE' ? 'IMAGE' : 'TEXT') : 'none'}
                            headerText={header?.text || ''}
                            headerImageUrl={header?.mediaUrl || header?.example?.header_url || ''}
                            body={textoDoBody(template.components)}
                            buttons={botoesDoTemplate(template.components)}
                            valores={valores}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={onFechar} className="text-gray-600 rounded-xl">Cancelar</Button>
                    <button onClick={handleEnviar} disabled={enviando} className={`${D.page.actionButton} disabled:opacity-60`}>
                        <Iconify icon={enviando ? 'line-md:loading-twotone-loop' : 'eva:paper-plane-outline'} width={16} />
                        {enviando ? 'Enviando...' : 'Enviar'}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

EnviarTesteDialog.propTypes = {
    aberto: PropTypes.bool,
    template: PropTypes.object,
    canal: PropTypes.object,
    pagina: PropTypes.shape({ nome: PropTypes.string, profile: PropTypes.string }),
    clienteId: PropTypes.string,
    onFechar: PropTypes.func.isRequired,
    onEnviado: PropTypes.func.isRequired
};
